import type { SynthPreset } from "../types";

/** Soft early-2000s keygen / cracktro pack — intimate chip-adjacent subtractive tones. */
export const KEYGEN_PRESETS: readonly SynthPreset[] = [
  {
    name: "Sub Root",
    osc1: { wave: "sine", octave: -1, semis: 0, detuneCents: 0, level: 0.62 },
    osc2: { wave: "tri", octave: -1, semis: 0, detuneCents: -4, level: 0.28 },
    filter: { type: "lowpass", cutoffHz: 320, resonance: 0.12 },
    ampEnv: { a: 0.008, d: 0.18, s: 0.72, r: 0.22 },
    modEnv: { a: 0.001, d: 0.14, s: 0, r: 0.06 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 0.8 }],
    glideMs: 18,
  },
  {
    name: "Warm Pluck Bass",
    osc1: { wave: "square", octave: -1, semis: 0, detuneCents: 0, level: 0.5, pwm: 0.42 },
    osc2: { wave: "tri", octave: -1, semis: 0, detuneCents: 6, level: 0.32 },
    filter: { type: "lowpass", cutoffHz: 520, resonance: 0.26 },
    ampEnv: { a: 0.003, d: 0.12, s: 0.55, r: 0.14 },
    modEnv: { a: 0.001, d: 0.09, s: 0, r: 0.04 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 1.6 }],
    glideMs: 10,
  },
  {
    name: "Pulse Lead",
    osc1: { wave: "square", octave: 0, semis: 0, detuneCents: 0, level: 0.46, pwm: 0.4 },
    osc2: { wave: "square", octave: 0, semis: 0, detuneCents: -7, level: 0.3, pwm: 0.55 },
    filter: { type: "lowpass", cutoffHz: 2200, resonance: 0.18 },
    ampEnv: { a: 0.015, d: 0.2, s: 0.6, r: 0.24 },
    modEnv: { a: 0.01, d: 0.25, s: 0.2, r: 0.12 },
    lfo: { rateHz: 4.5, wave: "tri", depth: 0.3 },
    routes: [
      { source: "lfo", target: "pwm", depth: 0.4 },
      { source: "modEnv", target: "cutoff", depth: 0.9 },
    ],
    glideMs: 24,
  },
  {
    name: "Chirp Arp",
    osc1: { wave: "square", octave: 1, semis: 0, detuneCents: 0, level: 0.44, pwm: 0.25 },
    osc2: { wave: "square", octave: 2, semis: 0, detuneCents: 5, level: 0.18, pwm: 0.5 },
    filter: { type: "lowpass", cutoffHz: 3400, resonance: 0.22 },
    ampEnv: { a: 0.001, d: 0.075, s: 0.12, r: 0.07 },
    modEnv: { a: 0.001, d: 0.05, s: 0, r: 0.03 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 0.4 },
      { source: "modEnv", target: "cutoff", depth: 1.4 },
    ],
  },
  {
    name: "Glass Bell",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0.42 },
    osc2: { wave: "sine", octave: 2, semis: 7, detuneCents: 4, level: 0.24 },
    filter: { type: "lowpass", cutoffHz: 3600, resonance: 0.08 },
    ampEnv: { a: 0.002, d: 0.5, s: 0.05, r: 0.45 },
    modEnv: { a: 0.001, d: 0.22, s: 0, r: 0.1 },
    lfo: { rateHz: 5.5, wave: "sine", depth: 0.1 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.7 },
      { source: "lfo", target: "pitch", depth: 0.1 },
    ],
  },
  {
    name: "Nostalgia Pad",
    osc1: { wave: "tri", octave: 0, semis: 0, detuneCents: -5, level: 0.36 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 8, level: 0.3 },
    filter: { type: "lowpass", cutoffHz: 1500, resonance: 0.16 },
    ampEnv: { a: 0.35, d: 0.6, s: 0.75, r: 0.9 },
    modEnv: { a: 0.4, d: 0.8, s: 0.45, r: 0.6 },
    lfo: { rateHz: 0.22, wave: "sine", depth: 0.3 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.1 },
      { source: "lfo", target: "cutoff", depth: 0.6 },
    ],
    glideMs: 60,
  },
  {
    name: "Airy Keys",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0.4 },
    osc2: { wave: "square", octave: 1, semis: 0, detuneCents: -6, level: 0.24, pwm: 0.5 },
    filter: { type: "lowpass", cutoffHz: 2800, resonance: 0.12 },
    ampEnv: { a: 0.02, d: 0.3, s: 0.5, r: 0.4 },
    modEnv: { a: 0.005, d: 0.35, s: 0.25, r: 0.2 },
    lfo: { rateHz: 0.8, wave: "tri", depth: 0.15 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.9 },
      { source: "lfo", target: "amp", depth: 0.12 },
    ],
  },
  {
    name: "Soft Kick",
    osc1: { wave: "sine", octave: -1, semis: 0, detuneCents: 0, level: 0.9 },
    osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.06 },
    filter: { type: "lowpass", cutoffHz: 600, resonance: 0.14 },
    ampEnv: { a: 0.001, d: 0.2, s: 0, r: 0.07 },
    modEnv: { a: 0.001, d: 0.06, s: 0, r: 0.01 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "pitch", depth: 24 },
      { source: "modEnv", target: "cutoff", depth: 1.5 },
    ],
  },
  {
    name: "Paper Snare",
    osc1: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.6 },
    osc2: { wave: "tri", octave: 0, semis: 3, detuneCents: 0, level: 0.16 },
    filter: { type: "bandpass", cutoffHz: 1900, resonance: 0.32 },
    ampEnv: { a: 0.001, d: 0.13, s: 0, r: 0.07 },
    modEnv: { a: 0.001, d: 0.07, s: 0, r: 0.02 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.2 },
      { source: "modEnv", target: "pitch", depth: 3 },
    ],
  },
  {
    name: "Tick Hat",
    osc1: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.5 },
    osc2: { wave: "square", octave: 2, semis: 0, detuneCents: 0, level: 0.08, pwm: 0.15 },
    filter: { type: "highpass", cutoffHz: 7200, resonance: 0.1 },
    ampEnv: { a: 0.001, d: 0.045, s: 0, r: 0.03 },
    modEnv: { a: 0.001, d: 0.03, s: 0, r: 0.015 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 0.5 }],
  },
];
